import React, { useState, useEffect, useMemo } from 'react'
import {
  ExternalLink,
  MessageSquare,
  Target,
  BarChart3,
  Award,
  TrendingUp,
  CheckCircle,
  Users,
  Star,
  Square,
  TreePine,
  HelpCircle,
  Zap,
  Loader2
} from 'lucide-react'
import { ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, Tooltip } from 'recharts'
import {
  calculateRealPerformanceDistribution,
  getEmployeesByPerformanceCategory,
  getAllSubordinatesInHierarchy,
  TECH_TEAM_PERFORMANCE,
  type MonumentaEmployee
} from '@/lib/real-data-service'
import { getDashboardConfigForEmployee } from '@/lib/services/employee.service'
import { useAuth } from '@/hooks/useAuth'

interface PerformanceTabProps {
  selectedPerformanceCategory: string | null
  onPerformanceCategoryClick: (category: string) => void
}

export function PerformanceTab({ selectedPerformanceCategory, onPerformanceCategoryClick }: PerformanceTabProps) {
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dashboardConfig, setDashboardConfig] = useState<any>(null)
  const [performanceDistribution, setPerformanceDistribution] = useState<any[]>([])
  const [employeesByCategory, setEmployeesByCategory] = useState<{ [key: string]: MonumentaEmployee[] }>({})

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        if (!user?.employee) {
          setError('User not authenticated or employee data not available')
          setIsLoading(false)
          return
        }

        // Load all data asynchronously
        const [config, distribution] = await Promise.all([
          getDashboardConfigForEmployee(user.employee),
          calculateRealPerformanceDistribution()
        ])

        setDashboardConfig(config)
        setPerformanceDistribution(distribution)

      } catch (err) {
        console.error('Error loading performance data:', err)
        setError((err as Error).message)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [user])

  // Load employees by category when needed
  useEffect(() => {
    if (!selectedPerformanceCategory || !user?.employee || !dashboardConfig) return

    const loadEmployeesByCategory = async () => {
      try {
        const allEmployeesInCategory = await getEmployeesByPerformanceCategory(selectedPerformanceCategory)
        const currentEmployeeId = parseInt(user.employee!.employeeId)

        let filteredEmployees: MonumentaEmployee[] = []

        // If admin, show all employees except themselves
        if (user.role === 'admin' || user.email === 'admin@monumenta.com') {
          filteredEmployees = allEmployeesInCategory.filter(emp => emp.id !== currentEmployeeId)
        }
        // If manager, show only direct reports that are in this category
        else if (dashboardConfig.isManager && dashboardConfig.directReports.length > 0) {
          const directReportIds = dashboardConfig.directReports.map((emp: any) => emp.id)
          filteredEmployees = allEmployeesInCategory.filter(emp =>
            directReportIds.includes(emp.id) && emp.id !== currentEmployeeId
          )
        }
        // For regular employees, return empty array (they don't manage anyone)
        else {
          filteredEmployees = []
        }

        setEmployeesByCategory(prev => ({
          ...prev,
          [selectedPerformanceCategory]: filteredEmployees
        }))
      } catch (err) {
        console.error('Error loading employees by category:', err)
      }
    }

    loadEmployeesByCategory()
  }, [selectedPerformanceCategory, user, dashboardConfig])

  // Memoized function to get filtered employees by category
  const getFilteredEmployeesByPerformanceCategory = useMemo(() => {
    return (category: string): MonumentaEmployee[] => {
      return employeesByCategory[category] || []
    }
  }, [employeesByCategory])

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="card flex items-center justify-center py-16">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-brand-primary animate-spin mx-auto mb-3" />
            <p className="text-brand-gray-light font-body">Carregando dados de performance...</p>
          </div>
        </div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="card flex items-center justify-center py-16">
          <div className="text-center">
            <div className="text-red-400 mb-3">Erro ao carregar dados</div>
            <p className="text-brand-gray-light font-body text-sm">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 bg-brand-primary text-white rounded-monumenta text-sm hover:bg-brand-primary/80 transition-colors"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 animate-fade-in-up">
      {/* Links para Qulture */}
      <div className="card">
        <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          Sistema Qulture - Gestão de Performance
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {[
            {
              id: 'feedback',
              name: 'Feedback',
              description: 'Sistema de feedback contínuo',
              icon: MessageSquare,
              color: 'text-brand-blue'
            },
            {
              id: 'pdi',
              name: 'PDI',
              description: 'Plano de Desenvolvimento Individual',
              icon: Target,
              color: 'text-success'
            },
            {
              id: 'okrs',
              name: 'OKRs',
              description: 'Objectives and Key Results',
              icon: BarChart3,
              color: 'text-brand-accent-600'
            },
            {
              id: 'avaliacao',
              name: 'Avaliação Semestral',
              description: 'Avaliação de performance',
              icon: Award,
              color: 'text-warning'
            },
            {
              id: 'clima',
              name: 'Clima',
              description: 'Pesquisa de clima organizacional',
              icon: TrendingUp,
              color: 'text-brand-primary'
            }
          ].map((link) => {
            const Icon = link.icon
            return (
              <div key={link.id} className="card-interactive group p-6 cursor-pointer">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="p-4 rounded-monumenta bg-brand-primary/10 group-hover:bg-brand-primary/20 transition-colors duration-200">
                    <Icon className={`w-8 h-8 ${link.color} group-hover:scale-110 transition-transform duration-200`} />
                  </div>
                  <div>
                    <h4 className="font-display font-semibold text-white mb-2 group-hover:text-brand-primary transition-colors duration-200">{link.name}</h4>
                    <p className="text-xs text-brand-gray-light font-body mb-4 leading-relaxed">{link.description}</p>
                    <button className="btn-ghost text-xs py-2 px-4 flex items-center gap-2">
                      <ExternalLink className="w-3 h-3" />
                      Acessar
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Performance da Equipe */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="card">
          <h3 className="font-display text-xl font-bold text-white mb-6 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            Performance da Equipe Técnica
          </h3>
          <div className="h-64 animate-fade-in">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={performanceDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {performanceDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1f2937', 
                    border: '1px solid #374151',
                    borderRadius: '12px',
                    color: 'white',
                    fontFamily: 'Inter, system-ui, sans-serif'
                  }} 
                />
              </RechartsPieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card">
          <h3 className="font-display text-xl font-bold text-white mb-6 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            Legenda de Performance
          </h3>
          <div className="space-y-3">
            {performanceDistribution.map((category) => {
              const iconMap = {
                'Star': Star,
                'Square': Square,
                'TrendingUp': TrendingUp,
                'TreePine': TreePine,
                'HelpCircle': HelpCircle,
                'Zap': Zap
              }
              const Icon = iconMap[category.icon as keyof typeof iconMap] || Star
              const isSelected = selectedPerformanceCategory === category.category
              
              return (
                <div 
                  key={category.category} 
                  className={`card-interactive group cursor-pointer p-4 transition-all duration-300 ${
                    isSelected 
                      ? 'border-brand-primary bg-brand-primary/5 shadow-monumenta-brand scale-105' 
                      : 'hover:scale-102'
                  }`}
                  onClick={() => onPerformanceCategoryClick(category.category)}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className={`p-3 rounded-monumenta ${isSelected ? 'shadow-monumenta-brand' : 'shadow-monumenta'} group-hover:scale-110 transition-transform duration-200`}
                      style={{ backgroundColor: category.color }}
                    >
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className={`font-display font-semibold ${isSelected ? 'text-brand-primary' : 'text-white'} group-hover:text-brand-primary transition-colors duration-200`}>
                          {category.category}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className={`font-display font-bold ${isSelected ? 'text-brand-primary' : 'text-brand-gray-light'} group-hover:text-brand-primary transition-colors duration-200`}>
                            {category.percentage}%
                          </span>
                          <span className={`text-xs font-body ${isSelected ? 'text-brand-primary' : 'text-brand-gray'} group-hover:text-brand-primary transition-colors duration-200`}>
                            ({category.value})
                          </span>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="mt-2 animate-fade-in">
                          <span className="text-xs text-brand-primary font-body font-medium bg-brand-primary/10 px-2 py-1 rounded-monumenta">
                            ✓ Filtro ativo - Ver funcionários abaixo
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Resumo de Avaliações */}
      <div className="card">
        <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          Resumo de Avaliações da Equipe
        </h3>
        <div className="dashboard-grid md:grid-cols-4">
          <div className="metric-card group text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-monumenta bg-success/10 group-hover:bg-success/20 transition-colors duration-200">
                <CheckCircle className="w-8 h-8 text-success group-hover:scale-110 transition-transform duration-200" />
              </div>
            </div>
            <p className="text-3xl font-bold font-display text-white mb-2 group-hover:scale-105 transition-transform duration-200">{TECH_TEAM_PERFORMANCE.totalEvaluationsCompleted}%</p>
            <p className="text-sm text-brand-gray-light font-body">Avaliações Concluídas</p>
          </div>
          
          <div className="metric-card group text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-monumenta bg-brand-blue/10 group-hover:bg-brand-blue/20 transition-colors duration-200">
                <Target className="w-8 h-8 text-brand-blue group-hover:scale-110 transition-transform duration-200" />
              </div>
            </div>
            <p className="text-3xl font-bold font-display text-white mb-2 group-hover:scale-105 transition-transform duration-200">{TECH_TEAM_PERFORMANCE.averageScore360}</p>
            <p className="text-sm text-brand-gray-light font-body">Nota Média 360°</p>
          </div>
          
          <div className="metric-card group text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-monumenta bg-brand-accent-600/10 group-hover:bg-brand-accent-600/20 transition-colors duration-200">
                <TrendingUp className="w-8 h-8 text-brand-accent-600 group-hover:scale-110 transition-transform duration-200" />
              </div>
            </div>
            <p className="text-3xl font-bold font-display text-white mb-2 group-hover:scale-105 transition-transform duration-200">{TECH_TEAM_PERFORMANCE.goalsAchieved}%</p>
            <p className="text-sm text-brand-gray-light font-body">Metas Atingidas</p>
          </div>
          
          <div className="metric-card group text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-monumenta bg-warning/10 group-hover:bg-warning/20 transition-colors duration-200">
                <Users className="w-8 h-8 text-warning group-hover:scale-110 transition-transform duration-200" />
              </div>
            </div>
            <p className="text-3xl font-bold font-display text-white mb-2 group-hover:scale-105 transition-transform duration-200">{TECH_TEAM_PERFORMANCE.teamSize}</p>
            <p className="text-sm text-brand-gray-light font-body">Pessoas na Equipe</p>
          </div>
        </div>
      </div>

      {/* Lista de Funcionários por Categoria - Aparece quando uma categoria é selecionada */}
      {selectedPerformanceCategory && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-xl font-bold text-white bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
              Funcionários - {selectedPerformanceCategory}
            </h3>
            <div className="flex items-center gap-4">
              <button
                onClick={() => onPerformanceCategoryClick(selectedPerformanceCategory)}
                className="btn-primary"
              >
                Limpar Filtro
              </button>
              <span className="text-sm text-brand-gray-light font-body bg-brand-dark/30 px-3 py-1 rounded-monumenta">
                {getFilteredEmployeesByPerformanceCategory(selectedPerformanceCategory).length} funcionários
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up">
            {getFilteredEmployeesByPerformanceCategory(selectedPerformanceCategory).map((employee) => (
              <div key={employee.id} className="card-interactive group p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-monumenta flex items-center justify-center text-brand-dark text-sm font-bold font-display shadow-monumenta group-hover:shadow-monumenta-brand transition-all duration-200">
                    {employee.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-display font-semibold truncate group-hover:text-brand-primary transition-colors duration-200">{employee.name}</h4>
                    <p className="text-sm text-brand-gray-light font-body truncate">{employee.role}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-brand-dark/30 rounded-monumenta">
                    <span className="text-brand-gray-light font-body text-sm">Departamento:</span>
                    <span className="text-white font-display font-medium text-sm">{employee.department}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-brand-dark/30 rounded-monumenta">
                    <span className="text-brand-gray-light font-body text-sm">Contrato:</span>
                    <span className="text-white font-display font-medium text-sm">{employee.contractType}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-brand-primary/10 border border-brand-primary/20 rounded-monumenta">
                    <span className="text-brand-gray-light font-body text-sm">Performance:</span>
                    <span className="text-brand-primary font-display font-bold text-sm">{employee.performance}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-brand-dark/30 rounded-monumenta">
                    <span className="text-brand-gray-light font-body text-sm">Cliente:</span>
                    <span className="text-white font-display font-medium text-sm truncate">{employee.client}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}